import { ReactNode } from "react";
import { PiStarFill } from "react-icons/pi";

const MIN_REVIEWS_TO_SHOW_RATING = 3;
const MIN_CLIENTS_COACHED_TO_SHOW = 20;
const MIN_YEARS_OF_WORK_EXPERIENCE_TO_SHOW = 5;
const MAX_YEARS_OF_WORK_EXPERIENCE_SHOWN = 30;

type CoachStatsRowProps = {
    averageRating: number | null;
    totalReviews: number;
    clientsCoached: number;
    yearsOfWorkExperience: number;
};

type Stat = {
    key: string;
    value: ReactNode;
    label: string;
};

export default function CoachStatsRow({
    averageRating,
    totalReviews,
    clientsCoached,
    yearsOfWorkExperience,
}: CoachStatsRowProps) {
    const stats: Stat[] = [];

    if (averageRating !== null && totalReviews >= MIN_REVIEWS_TO_SHOW_RATING) {
        stats.push({
            key: "rating",
            value: (
                <span className="flex items-center gap-1">
                    <PiStarFill className="text-brand-black" />
                    {averageRating.toPrecision(2)}
                </span>
            ),
            label: `${totalReviews} ratings`,
        });
    }

    if (clientsCoached >= MIN_CLIENTS_COACHED_TO_SHOW) {
        stats.push({
            key: "clientsCoached",
            value: `${clientsCoached}+`,
            label: "clients coached",
        });
    }

    if (yearsOfWorkExperience >= MIN_YEARS_OF_WORK_EXPERIENCE_TO_SHOW) {
        const cappedYears = Math.min(
            yearsOfWorkExperience,
            MAX_YEARS_OF_WORK_EXPERIENCE_SHOWN,
        );
        const yearsSuffix =
            yearsOfWorkExperience >= MAX_YEARS_OF_WORK_EXPERIENCE_SHOWN
                ? "+"
                : "";
        stats.push({
            key: "workExperience",
            value: `${cappedYears}${yearsSuffix} years`,
            label: "work experience",
        });
    }

    return (
        !!stats.length && (
            <div
                data-testid="coach-stats-row"
                className="mt-4 flex justify-around border-y-[0.5px] border-brand-border py-4 text-brand-black"
            >
                {stats.map((stat) => (
                    <div
                        key={stat.key}
                        className="flex flex-col items-center gap-1"
                    >
                        <div className="text-xl font-bold">{stat.value}</div>
                        <div className="text-sm text-brand-gray">
                            {stat.label}
                        </div>
                    </div>
                ))}
            </div>
        )
    );
}
